/** Status indicator component with colored dot and optional text. */
import React from "react";
import { Box, Chip, SxProps, Typography } from "@mui/material";
import { ServerStatus } from "../../types";

interface StatusIndicatorProps {
  status: ServerStatus;
  showText?: boolean;
  sx?: SxProps;
}

/** Status configuration with colors and labels. */
const STATUS_CONFIG: Record<
  ServerStatus,
  { color: "success" | "warning" | "error" | "default"; label: string; dotColor: string }
> = {
  [ServerStatus.ONLINE]: {
    color: "success",
    label: "Online",
    dotColor: "#4caf50",
  },
  [ServerStatus.DEGRADED]: {
    color: "warning",
    label: "Degraded",
    dotColor: "#ff9800",
  },
  [ServerStatus.DOWN]: {
    color: "error",
    label: "Down",
    dotColor: "#f44336",
  },
  [ServerStatus.OFFLINE]: {
    color: "default",
    label: "Offline",
    dotColor: "#9e9e9e",
  },
  [ServerStatus.PENDING]: {
    color: "default",
    label: "Pending",
    dotColor: "#9e9e9e",
  },
  [ServerStatus.DRAINING]: {
    color: "warning",
    label: "Draining",
    dotColor: "#ff9800",
  },
};

/** Status indicator showing colored dot and optional text label. */
export default function StatusIndicator({ status, showText = true, sx }: StatusIndicatorProps) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG[ServerStatus.OFFLINE];

  if (showText) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          ...sx,
        }}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            backgroundColor: config.dotColor,
            boxShadow: `0 0 4px ${config.dotColor}80`,
          }}
        />
        <Typography variant="body2" sx={{ color: "text.primary" }}>
          {config.label}
        </Typography>
      </Box>
    );
  }

  return (
    <Chip
      label={config.label}
      color={config.color}
      size="small"
      sx={{
        fontWeight: 500,
        "& .MuiChip-label": {
          px: 1,
        },
        ...sx,
      }}
    />
  );
}

/** Helper component for just the colored dot (no text). */
interface StatusDotProps {
  status: ServerStatus;
  size?: number;
  sx?: SxProps;
}

export function StatusDot({ status, size = 8, sx }: StatusDotProps) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG[ServerStatus.OFFLINE];

  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: "50%",
        backgroundColor: config.dotColor,
        boxShadow: `0 0 ${size}px ${config.dotColor}80`,
        ...sx,
      }}
    />
  );
}
